import { http } from './request';
import { CATEGORY_URLS as API_URLS } from './urls';
import type {
  CategoryCollectTaskStatus,
  CategoryCollectTaskType,
} from '../../common/enums/categoryCollectTask';
import type { LeadCategory } from './leads';

export interface InsightCategoryOption {
  value: LeadCategory;
  label: string;
  fullLabel?: string;
}

export interface InsightCategoryNode extends InsightCategoryOption {
  isLeaf: boolean;
  hasChildren: boolean;
  children: InsightCategoryNode[] | null;
}

export interface FetchInsightCategoriesParams {
  parentIds?: string[];
}

export interface InsightCategoryGroup {
  parentId: string;
  children: InsightCategoryNode[];
}

export interface InsightCategoriesResponse {
  parentIds: string[];
  list: InsightCategoryGroup[];
}

export interface FetchInsightCategorySearchParams {
  keyword: string;
  leafOnly?: boolean;
  limit?: number;
}

export interface InsightCategorySearchItem extends InsightCategoryOption {
  isLeaf: boolean;
  pathIds?: string[];
  pathLabels?: string[];
  fullLabel?: string;
}

export interface InsightCategorySearchResponse {
  total: number;
  list: InsightCategorySearchItem[];
}

export interface FetchProductCategoriesParams {
  keyword?: string;
  shopIds?: string[];
  page?: number;
  size?: number;
}

export interface ConfirmUserCategoriesPayload {
  categoryIds: string[];
}

export interface ProductCategoryListItem {
  categoryId: string;
  label: string;
  fullLabel?: string;
}

export interface ProductCategoriesResponse {
  status?: string;
  confirmed?: boolean;
  total: number;
  list: ProductCategoryListItem[];
  recommendedList?: ProductCategoryListItem[];
  maxCount?: number;
  computeCharge?: {
    charged?: boolean;
    skipped?: boolean;
    freeQuota?: boolean;
    freeQuotaUsed?: number;
    freeQuotaTotal?: number;
    freeQuotaRemaining?: number;
    computeDelta?: number;
    scenarioCode?: string;
    reason?: string;
  };
}

export interface ConfirmShopCategoriesPayload {
  shopId: string;
  categoryIds: string[];
}

export type CategoryUserProfilePlatform = 'douyin';

export interface FetchCategoryUserProfilesPayload {
  platform: CategoryUserProfilePlatform;
  keyword: string;
}

export interface CategoryUserProfileItem {
  platform: string;
  keyword: string;
  secUid: string;
  nickname: string;
  avatarUrl: string;
  uniqueId: string;
  videoTitle: string;
  videoAuthorName: string;
  signature: string;
  followerCount: number;
}

export interface CategoryUserProfilesResponse {
  list: CategoryUserProfileItem[];
}

export type CategoryCollectTaskTargetType = 'CATEGORY' | 'COMPETITOR_ACCOUNT' | 'ECOM_ACCOUNT';

export interface CategoryCollectTask {
  taskId: string;
  parentTaskId?: string | null;
  taskType: CategoryCollectTaskType;
  taskTypeLabel?: string;
  targetType: CategoryCollectTaskTargetType;
  targetKey: string;
  /** 后端部分异步任务返回 targetKv，语义等同于可匹配的目标 key。 */
  targetKv?: string;
  targetLabel?: string;
  reusedFromTaskId?: string | null;
  reused?: boolean;
  sourceType?: string;
  sourceId?: string;
  status: CategoryCollectTaskStatus;
  active?: boolean;
  triggerSource?: string;
  errorMessage?: string;
  summary?: Record<string, unknown>;
  createTime?: number;
  updateTime?: number;
  startTime?: number;
  finishTime?: number;
  durationSeconds?: number;
  count?: number;
  message?: string;
}

export interface FetchCategoryCollectTasksParams {
  taskIds?: string[];
  statuses?: CategoryCollectTaskStatus[];
  limit?: number;
}

export interface CategoryCollectTasksResponse {
  total: number;
  list: CategoryCollectTask[];
}

/**
 * Insight 类目列表
 */
export const fetchInsightCategories = (params: FetchInsightCategoriesParams = {}) => {
  const query: Record<string, unknown> = {};
  const parentIds = params.parentIds?.filter((item) => item.trim());
  if (parentIds?.length) {
    query['parentIds[]'] = parentIds.join(',');
  }
  return http.get<InsightCategoriesResponse>(API_URLS.insightCategories, { params: query });
};

/**
 * 类目搜索
 */
export const fetchInsightCategorySearch = (params: FetchInsightCategorySearchParams) => {
  return http.get<InsightCategorySearchResponse>(API_URLS.insightCategoriesSearch, { params });
};

/**
 * 当前执行中的类目采集子任务
 */
export const fetchCategoryCollectTasks = (params: FetchCategoryCollectTasksParams = {}) => {
  const query: { taskIds?: string; statuses?: string; limit?: number } = {
    limit: params.limit ?? 100,
  };

  if (params.taskIds?.length) {
    query.taskIds = params.taskIds.join(',');
  }

  if (params.statuses?.length) {
    query.statuses = params.statuses.join(',');
  }

  return http.get<CategoryCollectTasksResponse>(API_URLS.categoryCollectTasks, { params: query });
};

export const fetchCategoryCollectTask = (taskId: string) => {
  return http.get<CategoryCollectTask>(`${API_URLS.categoryCollectTasks}/${encodeURIComponent(taskId)}`);
};

/**
 * 当前系统账号下的货品关注类目并集
 */
export const fetchProductCategories = (params: FetchProductCategoriesParams = {}) => {
  const query: Record<string, unknown> = {
    page: params.page ?? 1,
    size: params.size ?? 200,
  };

  if (params.keyword) {
    query.keyword = params.keyword;
  }

  if (params.shopIds?.length) {
    query.shopIds = params.shopIds.join(',');
  }

  return http.get<ProductCategoriesResponse>(API_URLS.productCategories, { params: query });
};

export const fetchShopCategories = (params: FetchProductCategoriesParams = {}) => {
  const query: Record<string, unknown> = {
    page: params.page ?? 1,
    size: params.size ?? 200,
  };

  if (params.keyword) {
    query.keyword = params.keyword;
  }

  if (params.shopIds?.length) {
    query.shopIds = params.shopIds.join(',');
  }

  return http.get<ProductCategoriesResponse>(API_URLS.shopCategories, { params: query });
};

export const fetchUserCategories = (params: FetchProductCategoriesParams = {}) => {
  const query: Record<string, unknown> = {
    page: params.page ?? 1,
    size: params.size ?? 200,
  };

  if (params.keyword) {
    query.keyword = params.keyword;
  }

  return http.get<ProductCategoriesResponse>(API_URLS.userCategories, { params: query });
};

export const confirmUserCategories = (payload: ConfirmUserCategoriesPayload) => {
  return http.post<ProductCategoriesResponse>(API_URLS.confirmUserCategories, payload);
};

export const confirmShopCategories = (payload: ConfirmShopCategoriesPayload) => {
  return http.post<ProductCategoriesResponse>(API_URLS.confirmShopCategories, payload);
};

/**
 * 类目关联竞对账号列表
 */
export const fetchCategoryUserProfiles = (payload: FetchCategoryUserProfilesPayload) => {
  return http.post<CategoryUserProfilesResponse>(API_URLS.categoryUserProfiles, payload);
};
